import { safeParse, safeStringify } from 'utils';
import { ELEMENT_TYPES } from '../constants';

const countOnArray = (array: any[], currentCharCount = 0) =>
  array.reduce(
    (acc, child) => acc + countTextLength(child, currentCharCount),
    0,
  );

export const countTextLength = (
  val: Record<string, any>,
  currentCount = 0,
): number => {
  if (!val) return 0;

  if (Array.isArray(val)) return countOnArray(val, currentCount);

  if (Array.isArray(val.children)) {
    if (val.type === ELEMENT_TYPES.MENTION)
      return currentCount + (val.character.length || 0) + 1;

    return countOnArray(val.children, currentCount);
  }

  return val.text.length;
};

export const countTextLengthFromJson = (text: string) =>
  countTextLength(safeParse(text));

export const getIsEditorValueEmpty = (val: Record<string, any>): boolean => {
  if (!val) return true;
  if (Array.isArray(val)) return val.every(getIsEditorValueEmpty);
  if (Array.isArray(val.children))
    return !val.character && val.children.every(getIsEditorValueEmpty);
  return !val.text?.trim();
};

// Trim leading and trailing empty nodes from rich text editor value
const trimRichTextValue = (value: any) => {
  if (!value || !Array.isArray(value)) return value;

  // Find first non-empty node
  let startIndex = 0;
  while (
    startIndex < value.length &&
    getIsEditorValueEmpty(value[startIndex])
  ) {
    startIndex++;
  }

  // Find last non-empty node
  let endIndex = value.length - 1;
  while (endIndex >= 0 && getIsEditorValueEmpty(value[endIndex])) {
    endIndex--;
  }

  // If all nodes are empty, return a single empty node
  if (startIndex > endIndex) {
    return [{ type: ELEMENT_TYPES.DIV, children: [{ text: '' }] }];
  }

  return value.slice(startIndex, endIndex + 1);
};

// Trim rich text editor value from JSON string and return JSON string
export const trimRichTextValueFromJson = (text: string) =>
  safeStringify(trimRichTextValue(safeParse(text)));

const flattenRichTextNode = (node: any): string => {
  if (!node) return '';

  if (Array.isArray(node)) {
    return node.map(flattenRichTextNode).filter(Boolean).join(' ');
  }

  if (Array.isArray(node.children)) {
    if (node.type === ELEMENT_TYPES.MENTION) {
      return node.character || '';
    }

    const joiner =
      node.type === ELEMENT_TYPES.ORDERED_LIST ||
      node.type === ELEMENT_TYPES.UNORDERED_LIST
        ? ' '
        : '';

    return node.children.map(flattenRichTextNode).join(joiner);
  }

  return node.text || '';
};

export const richTextJsonToPlainText = (text = '') =>
  flattenRichTextNode(safeParse(text)).trim();
